import { ListIcon, PlusIcon, Settings2Icon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import useSWR from 'swr'

import {
  PreferenceButton,
  PreferenceCategory,
  PreferenceInfo,
  PreferenceLink,
  PreferenceScreen,
} from '../../components/Preference'
import { fetcher } from '../../lib/swr'
import type { FullList } from '../../pg/types'

const ACCESS_LABEL: Record<string, string> = {
  tenant: 'Alleen huishouden',
  follower: 'Volgers',
  everyone: 'Iedereen',
}

export default function ListsSettings() {
  const navigate = useNavigate()
  const lists = useSWR<FullList[]>('/api/lists', fetcher)
  const rows = lists.data ?? []
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (lists.error) {
      toast.error(lists.error.message || 'Kon lijsten niet laden')
    }
  }, [lists.error])

  async function makeList() {
    setBusy(true)
    try {
      const res = await fetch('/api/lists', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          display: 'Nieuwe lijst',
          accessLevel: 'everyone',
        }),
      })
      const data = await res.json()
      if (!res.ok) {
        toast.error(data.message || 'Mislukt')
        return
      }
      await lists.mutate()
      toast.success('Lijst gemaakt')
      navigate(`/settings/lists/${data.id}`)
    } finally {
      setBusy(false)
    }
  }

  async function move(listId: number, direction: 'up' | 'down') {
    const res = await fetch(`/api/lists/${listId}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reorder: direction }),
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      toast.error(data.message || 'Volgorde wijzigen mislukt')
      return
    }
    await lists.mutate()
  }

  return (
    <PreferenceScreen title="Lijsten" backHref="/settings">
      <PreferenceCategory filled>
        <PreferenceButton
          icon={<PlusIcon className="h-6 w-6" />}
          title="Nieuwe lijst"
          summary="Summer, cocktails, quick, …"
          onPress={makeList}
          disabled={busy}
        />
      </PreferenceCategory>

      <PreferenceCategory title="Jouw lijsten" filled>
        {rows.length === 0 ? (
          <PreferenceInfo
            icon={<ListIcon className="h-6 w-6" />}
            title="Nog geen lijsten"
            summary="Maak er een om recepten te bundelen."
          />
        ) : (
          rows.map((list, index) => (
            <div key={list.id} className="relative">
              <PreferenceLink
                icon={<Settings2Icon className="h-6 w-6" />}
                title={list.display}
                summary={`${list.recipes.length} recept${list.recipes.length === 1 ? '' : 'en'} · ${ACCESS_LABEL[list.accessLevel] || list.accessLevel}`}
                href={`/settings/lists/${list.id}`}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex flex-col opacity-30 hover:opacity-70 transition-opacity">
                <button
                  type="button"
                  aria-label="Omhoog"
                  disabled={index === 0}
                  className="text-[10px] leading-none px-1 py-0.5 disabled:opacity-20"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    void move(list.id, 'up')
                  }}
                >
                  ▲
                </button>
                <button
                  type="button"
                  aria-label="Omlaag"
                  disabled={index === rows.length - 1}
                  className="text-[10px] leading-none px-1 py-0.5 disabled:opacity-20"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    void move(list.id, 'down')
                  }}
                >
                  ▼
                </button>
              </div>
            </div>
          ))
        )}
      </PreferenceCategory>
    </PreferenceScreen>
  )
}
